import React, { useState, useEffect, useCallback } from "react";
import { SortAsc, Clock, CheckSquare, Square, X } from "lucide-react";
import "./SubmissionViewerModal.css";

type AuditDoc = {
  submissionId: number;
  url: string;
  time: string;
  name: string;
};

interface Props {
  show: boolean;
  onClose: () => void;
}

const PROXY_BASE = "https://docuseal-proxy.t9e2i3kal.workers.dev/docuseal";

// Simple loading modal component
const LoadingModal = ({ message }: { message: string }) => (
  <div className="loading-modal-overlay">
    <div className="loading-modal-content">
      <div className="loading-spinner">⏳</div>
      <div className="loading-message">{message}</div>
    </div>
  </div>
);

// Simple close button component
const ModalCloseButton = ({ onClick }: { onClick: () => void }) => (
  <button className="modal-close-button" onClick={onClick}>
    ×
  </button>
);

// PDF Viewer Modal Component
const PdfViewerModal = ({ url, onClose }: { url: string; onClose: () => void }) => (
  <div className="pdf-viewer-overlay">
    <div className="pdf-viewer-modal">
      <button className="pdf-close-button" onClick={onClose}>
        <X size={24} />
      </button>
      <div className="pdf-viewer-content">
        <iframe
          src={url}
          className="pdf-frame"
          title="PDF Document"
          frameBorder="0"
        />
      </div>
    </div>
  </div>
);

const SubmissionViewerModal: React.FC<Props> = ({ show, onClose }) => {
  const [auditDocs, setAuditDocs] = useState<AuditDoc[]>([]);
  const [filteredDocs, setFilteredDocs] = useState<AuditDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortByTime, setSortByTime] = useState(false); // false = alphabetical, true = time
  const [searchTerm, setSearchTerm] = useState("");
  const [checkedSubmissions, setCheckedSubmissions] = useState<Set<number>>(
    new Set()
  );
  const [showAllRecords, setShowAllRecords] = useState(true);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState<string | null>(null);
  const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false);

  /* ——————————— Correct DocuSeal API approach ——————————— */
  const fetchToday = useCallback(async () => {
    setLoading(true);
    try {
      const raw = localStorage.getItem("uploadAuth");
      if (!raw) throw new Error("uploadAuth missing");
      const { guid } = JSON.parse(raw);
      if (!guid) throw new Error("guid missing");

      /* last 24 h window */
      const end = new Date();
      const start = new Date(end.getTime() - 86_400_000);
      const fmt = (d: Date) =>
        `${d.toISOString().slice(0, 19).replace("T", " ")}`;

      console.log("Fetching submitters from:", fmt(start), "to", fmt(end));

      /* Step 1: Get submitters who completed in the last 24h */
      const submitterQS = {
        completed_after: fmt(start),
        completed_before: fmt(end),
        limit: "100",
      };

      let after: string | undefined;
      const allSubmitters: any[] = [];

      // Paginate through submitters
      while (true) {
        const qs = new URLSearchParams(submitterQS);
        if (after) qs.set("after", after);

        const res = await fetch(`${PROXY_BASE}/submitters?${qs}`, {
          headers: { "X-Auth-Guid": guid },
        });

        if (!res.ok) throw new Error(`/submitters → ${res.status}`);

        const json = await res.json();
        console.log(`Submitters batch: ${json.data.length} found`);

        if (json.data.length === 0) break;
        allSubmitters.push(...json.data);

        const next = json.pagination?.next;
        if (!next || next === after) break;
        after = String(next);
      }

      console.log(`Total submitters in date range: ${allSubmitters.length}`);

      /* Step 2: Extract unique submission IDs */
      const submissionIds = [
        ...new Set(allSubmitters.map((submitter) => submitter.submission_id)),
      ];

      console.log(`Unique submissions: ${submissionIds.length}`);

      /* Step 3: Fetch full submission details */
      const submissions: any[] = [];

      // Fetch submissions in parallel (but limit concurrency)
      const batchSize = 10;
      for (let i = 0; i < submissionIds.length; i += batchSize) {
        const batch = submissionIds.slice(i, i + batchSize);

        const batchPromises = batch.map(async (submissionId) => {
          try {
            const res = await fetch(
              `${PROXY_BASE}/submissions/${submissionId}`,
              {
                headers: { "X-Auth-Guid": guid },
              }
            );

            if (!res.ok) {
              console.warn(
                `Failed to fetch submission ${submissionId}: ${res.status}`
              );
              return null;
            }

            return await res.json();
          } catch (error) {
            console.warn(`Error fetching submission ${submissionId}:`, error);
            return null;
          }
        });

        const batchResults = await Promise.all(batchPromises);
        submissions.push(...batchResults.filter((s) => s !== null));

        console.log(
          `Fetched ${submissions.length}/${submissionIds.length} submissions`
        );
      }

      /* Step 4: Filter and process for audit logs */
      const docs: AuditDoc[] = [];
      submissions.forEach((submission: any) => {
        if (submission.audit_log_url && submission.status === "completed") {
          // Get submitter name from the submission
          const submitterName =
            submission.submitters?.[0]?.name || `Submission #${submission.id}`;

          docs.push({
            submissionId: submission.id,
            url: submission.audit_log_url,
            time: submission.completed_at,
            name: submitterName,
          });
        }
      });

      console.log(`Final audit docs: ${docs.length}`);

      /* Sort */
      if (sortByTime) {
        docs.sort((a, b) => Date.parse(b.time) - Date.parse(a.time));
      } else {
        docs.sort((a, b) => a.name.localeCompare(b.name));
      }

      setAuditDocs(docs);
      setFilteredDocs(docs);
    } catch (err) {
      console.error("Failed to load audit logs:", err);
      setAuditDocs([]);
    } finally {
      setLoading(false);
    }
  }, [sortByTime]);

  /* load checked submissions from localStorage */
  useEffect(() => {
    const loadCheckedSubmissions = () => {
      try {
        const stored = localStorage.getItem("checkedSubmissions");
        if (stored) {
          const submissionIds = JSON.parse(stored);
          setCheckedSubmissions(new Set(submissionIds));
        }
      } catch (error) {
        console.error("Failed to load checked submissions:", error);
      }
    };

    loadCheckedSubmissions();
  }, []);

  /* save checked submissions to localStorage */
  const toggleSubmissionCheck = (submissionId: number) => {
    setCheckedSubmissions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(submissionId)) {
        newSet.delete(submissionId);
      } else {
        newSet.add(submissionId);
      }

      // Persist to localStorage
      try {
        localStorage.setItem(
          "checkedSubmissions",
          JSON.stringify(Array.from(newSet))
        );
      } catch (error) {
        console.error("Failed to save checked submissions:", error);
      }

      return newSet;
    });
  };

  /* reload each time the modal appears */
  useEffect(() => {
    if (show) fetchToday();
  }, [show, fetchToday]);

  /* PDF viewing functions */
  const openPdfViewer = (url: string) => {
    setSelectedPdfUrl(url);
    setIsPdfViewerOpen(true);
  };

  const closePdfViewer = () => {
    setIsPdfViewerOpen(false);
    setSelectedPdfUrl(null);
  };

  /* re-sort when sort type changes */
  useEffect(() => {
    if (auditDocs.length > 0) {
      const sortedDocs = [...auditDocs];
      if (sortByTime) {
        sortedDocs.sort((a, b) => Date.parse(b.time) - Date.parse(a.time));
      } else {
        sortedDocs.sort((a, b) => a.name.localeCompare(b.name));
      }
      setAuditDocs(sortedDocs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortByTime]);

  /* filter documents based on showAllRecords toggle */
  const getFilteredDocs = () => {
    if (showAllRecords) {
      return filteredDocs;
    }
    // Only show unchecked submissions
    return filteredDocs.filter(
      (doc) => !checkedSubmissions.has(doc.submissionId)
    );
  };

  const visibleDocs = getFilteredDocs();

  /* ——————————— UI ——————————— */
  const end = new Date();
  const start = new Date(end.getTime() - 86_400_000);
  const rangeLabel =
    `${start.toLocaleDateString()} ${start.toLocaleTimeString()} – ` +
    `${end.toLocaleDateString()} ${end.toLocaleTimeString()}`;

  return show ? (
    <>
      {isPdfViewerOpen && selectedPdfUrl && (
        <PdfViewerModal url={selectedPdfUrl} onClose={closePdfViewer} />
      )}
      <div className="submission-modal-overlay">
        {loading && <LoadingModal message="Loading last 24 h audit logs…" />}
        <div className="submission-modal">
          <ModalCloseButton onClick={onClose} />

        <div className="modal-header">
          <h2>Waivers(last 24 h)</h2>
          <button
            onClick={() => setSortByTime(!sortByTime)}
            className="sort-button large-button"
            title={sortByTime ? "Sort alphabetically" : "Sort by time"}
          >
            {sortByTime ? <SortAsc size={20} /> : <Clock size={20} />}
            <span className="button-text">{sortByTime ? "A-Z" : "Time"}</span>
          </button>
          <button
            onClick={() => setShowAllRecords(!showAllRecords)}
            className="view-toggle-button large-button"
            title={showAllRecords ? "Hide checked records" : "Show all records"}
          >
            <span className="button-icon">{showAllRecords ? "👁️" : "👁️"}</span>
            <span className="button-text">{showAllRecords ? "All" : "Active"}</span>
          </button>
          <button
            onClick={fetchToday}
            disabled={loading}
            className="refresh-button large-button"
            title="Refresh"
          >
            <span className="button-icon">🔄</span>
            <span className="button-text">Refresh</span>
          </button>
        </div>

        <div className="search-container">
          <input
            type="text"
            placeholder="Search by name..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => {
              const term = e.target.value;
              setSearchTerm(term);
              setFilteredDocs(
                auditDocs.filter((doc) =>
                  doc.name.toLowerCase().includes(term.toLowerCase())
                )
              );
            }}
          />
        </div>

        <p className="audit-count">
          {rangeLabel} — {visibleDocs.length} record
          {visibleDocs.length !== 1 && "s"}
          {!showAllRecords && filteredDocs.length !== visibleDocs.length && (
            <span className="checked-records-info">
              ({checkedSubmissions.size} checked,{" "}
              {filteredDocs.length - visibleDocs.length} hidden)
            </span>
          )}
        </p>

        {!loading && (
          <div className="submission-modal-content">
            {visibleDocs.length === 0 && (
              <p>
                {filteredDocs.length > 0
                  ? "All records are checked. Toggle 'View All Records' to see them."
                  : "No audit logs found in the last 24 h."}
              </p>
            )}

            <div className="audit-grid">
              {visibleDocs.map((doc) => (
                <div key={doc.submissionId} className="audit-card">
              <div className="audit-card-header">
                <button
                  onClick={() => openPdfViewer(doc.url)}
                  className="doc-link"
                  title={`View ${doc.name}`}
                >
                  {doc.name}
                </button>
                <button
                  onClick={() => toggleSubmissionCheck(doc.submissionId)}
                  className="check-button large-icon"
                  title={
                    checkedSubmissions.has(doc.submissionId)
                      ? "Uncheck record"
                      : "Check record"
                  }
                >
                  {checkedSubmissions.has(doc.submissionId) ? (
                    <CheckSquare size={24} color="#00ff00" />
                  ) : (
                    <Square size={24} color="#666" />
                  )}
                </button>
              </div>
              <div className="submission-id">
                Submission #{doc.submissionId}
              </div>
              <div className="submission-time">
                {new Date(doc.time).toLocaleTimeString()}
              </div>
            </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  ) : null;
};

export default SubmissionViewerModal;
